/**
 * Creates and returns a new HTML element based on the provided options.
 *
 * @param {Object} options - The options for creating the HTML element.
 * @param {string} options.tag - The type of element to create (e.g., 'div', 'span').
 * @param {string | string[]} [options.className] - The class or classes to add to the element.
 * @param {string} [options.id] - The id to assign to the element.
 * @param {string} [options.text] - The text content to add to the element.
 * @param {{ [key: string]: string }} [options.attr] - The attributes to add to the element.
 * @param {{ [key: string]: string }} [options.dataset] - The data attributes to add to the element.
 * @returns {HTMLElement} The created HTML element.
 */
export function createEl({
  tag,
  className,
  id,
  text,
  attr,
  dataset,
}: {
  tag: string;
  className?: string | string[];
  id?: string;
  text?: string;
  attr?: { [key: string]: string };
  dataset?: { [key: string]: string };
}) {
  const elem = document.createElement(tag);

  if (className) {
    if (typeof className === 'string') {
      elem.classList.add(className);
    } else {
      elem.classList.add(...className);
    }
  }

  if (id) {
    elem.id = id;
  }

  if (text) {
    const textNode = document.createTextNode(text);
    elem.appendChild(textNode);
  }

  if (attr) {
    Object.entries(attr).forEach(([key, val]) => elem.setAttribute(key, val));
  }

  if (dataset) {
    Object.entries(dataset).forEach(([key, val]) => elem.setAttribute(`data-${key}`, val));
  }

  return elem;
}
